import { Sparkles, Shield, Heart, Stethoscope, BadgeCheck, Globe2, ShieldCheck, Star } from "lucide-react";
import { motion, Variants } from "framer-motion";
import FloatingParticles from "@/components/ui/floating-particles";
import SmartSearchTabs from "@/components/home/SmartSearchTabs";

const Hero = () => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
  };
  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
  };

  const trustBadges = [
    { icon: BadgeCheck, label: "NABH Verified" },
    { icon: ShieldCheck, label: "AI Verified" },
    { icon: Shield, label: "Secure & Encrypted" },
  ];

  return (
    <section className="relative overflow-hidden min-h-[80vh] sm:min-h-[85vh] lg:min-h-[92vh] flex items-center">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{ background: "var(--ui-gradient-hero, var(--gradient-hero))" }} />
        <motion.div
          className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)" }}
          animate={{ scale: [1, 1.2, 1], x: [0, 50, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute inset-0 opacity-20"
          style={{ background: "radial-gradient(circle at 80% 80%, rgba(255,255,255,0.4) 0%, transparent 40%)" }}
          animate={{ scale: [1.2, 1, 1.2], y: [0, -30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <FloatingParticles />

      {/* ECG line */}
      <div className="absolute inset-0 opacity-[0.06]">
        <svg viewBox="0 0 1440 200" className="absolute bottom-1/3 w-full" preserveAspectRatio="none">
          <motion.path
            d="M0,100 L200,100 L220,40 L240,160 L260,80 L280,120 L300,100 L500,100 L520,40 L540,160 L560,80 L580,120 L600,100 L800,100 L820,40 L840,160 L860,80 L880,120 L900,100 L1100,100 L1120,40 L1140,160 L1160,80 L1180,120 L1200,100 L1440,100"
            fill="none" stroke="white" strokeWidth="2"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
        </svg>
      </div>

      {/* Floating medical icons */}
      <motion.div
        className="absolute top-24 left-10 md:left-20 w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl hidden md:flex items-center justify-center"
        animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <Heart className="w-8 h-8 text-white animate-heartbeat" />
      </motion.div>
      <motion.div
        className="absolute top-40 right-10 md:right-32 w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl hidden md:flex items-center justify-center"
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        <Stethoscope className="w-7 h-7 text-white" />
      </motion.div>

      <div className="container mx-auto px-4 py-14 sm:py-20 lg:py-24 relative z-10">
        <motion.div className="max-w-5xl mx-auto text-center" variants={containerVariants} initial="hidden" animate="visible">
          {/* Top utility row: country selector + badge */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-2 mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-medium">
              <Globe2 className="w-3.5 h-3.5" />
              India
            </span>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-pill px-4 py-1.5">
              <motion.span
                className="w-2 h-2 rounded-full bg-healing-green-light"
                animate={{ scale: [1, 1.4, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <Sparkles className="w-3.5 h-3.5 text-white/90" />
              <span className="text-white/90 text-xs font-medium tracking-wide">
                AI-Powered Healthcare Operating System
              </span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h1
            variants={itemVariants}
            className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-5 leading-[1.1] tracking-tight"
          >
            Find <span className="relative inline-block">
              <span className="relative z-10">Doctors, Hospitals</span>
              <motion.span
                className="absolute -bottom-2 left-0 right-0 h-3 bg-healing-green/40 rounded-pill -z-0"
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              />
            </span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-healing-green-light to-white">
              & Healthcare Services Near You
            </span>
          </motion.h1>

          <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <span className="text-white/80 text-xs sm:text-sm font-medium">
              Trusted by <span className="text-white font-semibold">50,000+</span> patients · <span className="text-white font-semibold">22 cities</span>
            </span>
          </motion.div>

          <motion.p
            variants={itemVariants}
            className="text-base sm:text-lg md:text-xl text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed"
          >
            Search across an intelligent network of verified hospitals, doctors, pharmacies, diagnostics, blood banks,
            ambulance services and homecare — powered by PataKare AI.
          </motion.p>

          {/* Smart Search */}
          <motion.div variants={itemVariants}>
            <SmartSearchTabs />
          </motion.div>

          {/* Trust badges */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-8"
          >
            {trustBadges.map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-white/10 backdrop-blur-md border border-white/20 text-white/85 text-[11px] sm:text-xs font-medium"
              >
                <b.icon className="w-3.5 h-3.5 text-healing-green-light" />
                {b.label}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" className="w-full">
          <motion.path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            className="fill-background"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: 0.5 }}
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
